import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef} from '@angular/core';
import {FinanceProfileModel, FinanceWalletModel, ForecastModel} from '../../../../models/finance.model';
import {ModalService} from '../../../../services/modal/modal.service';
import {FinanceService} from '../../../../services/api/finance.service';
import {AlertService} from '../../../../services/notify/alert.service';
import {HelpersService} from '../../../../services/helpers.service';
import {TranslateService} from '@ngx-translate/core';
import {of, switchMap} from 'rxjs';

interface StructWalletInfo {
    totalBlocked: number;
    promised: number;
    promisedBlocked: number;
    expected: number;
    expectedBlocked: number;
}
interface IButtonsDisabled  {
    replenishment?:boolean;
    barter?:boolean;
    payment?:boolean;
    history?:boolean;
}

@Component({
    selector: 'app-slave-wallet',
    templateUrl: './slave-wallet.component.html',
    styleUrls: ['./slave-wallet.component.scss'],
})
export class SlaveWalletComponent implements OnInit, OnChanges {
    @Input() contractView = false;
    @Input() isLoadingData = true;
    @Input() isLoadingDataError = false;
    @Input() wallet: FinanceWalletModel;
    @Input() masterWallet: FinanceWalletModel;
    @Input() i = 0;
    @Input() forecastData: ForecastModel[];
    @Input() financeProfile: FinanceProfileModel | { wallets: FinanceWalletModel[] };
    @Input() isPrivate = false;
    @Input() buttonsDisabled:IButtonsDisabled|null = null;

    @Output() emitRefresh: EventEmitter<any> = new EventEmitter<any>();

    structWalletsInfo: {
        [key: string]: StructWalletInfo
    } = {};
    isShowReplenishmentModal = false;
    isShowPayoutModal = false;
    isShowBarterModal = false;
    showWallet = false;

    changeMode = true;

    constructor(
        public modalService: ModalService,
        public helper: HelpersService,
        protected financeService: FinanceService,
        protected alert: AlertService,
        public translate: TranslateService
    ) {
    }

    ngOnInit(): void {
        if (!this.contractView) {
            this.showWallet = this.wallet.is_master;
        } else {
            this.showWallet = true;
        }
        if (this.wallet.is_master) {
            this.loadForecastWallets(this.wallet.guid);
        }

        if (!this.forecastData) {
            of(this.isPrivate)
                .pipe(
                    switchMap((person) => person ? this.financeService.getForecastPrivateWallets() : this.financeService.getMoneyInfo()))
                .subscribe(res => {
                    this.forecastData = res.forecast.forecast_wallets;
                    if (this.contractView) {
                        this.loadForecastWallets(this.wallet.guid);
                    }
                });
        }

        if (!this.contractView) {
            this.loadForecastWallets(this.wallet.guid);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.wallet && !changes.wallet.firstChange && this.showWallet) {
            this.loadForecastWallets(changes.wallet.currentValue.guid);
        }
        if (changes.forecastData && !changes.forecastData.firstChange && this.showWallet) {
            this.forecastData = changes.forecastData.currentValue;
        }
    }

    getWallet(): void {
        this.financeService.getWalletByGuid(this.wallet.guid).subscribe({
            next: res => {
                this.wallet = res.wallet;
                this.loadForecastWallets(this.wallet.guid);
            }
        });
    }

    swapSlider(): void {
        if (this.contractView) {
            return;
        }
        if (!this.structWalletsInfo[this.wallet.guid]) {
            this.loadForecastWallets(this.wallet.guid);
        }
        this.showWallet = !this.showWallet;
    }

    loadForecastWallets(guid: string): void {
        const forecastWallet = structuredClone(this.forecastData.find(forecast => forecast && forecast.wallet.guid === guid));
        this.structWalletsInfo[guid] = {
            expectedBlocked: forecastWallet ? Number(forecastWallet.incoming_approved_total) : 0,
            expected: forecastWallet ? Number(forecastWallet.incoming_promised_total) : 0,
            promisedBlocked: forecastWallet ? Number(forecastWallet.outcoming_approved_total) : 0,
            promised: forecastWallet ? Number(forecastWallet.outcoming_promised_total) : 0,
            totalBlocked: forecastWallet ? Number(forecastWallet.outcoming_approved_total) : 0,
        };
    }


    deleteWallet(wallet: FinanceWalletModel): void {
        if (wallet.total > 0) {
            return this.alert.setAlert(this.translate.instant('ALERTS.DELETE_WALLET'), 'blue');
        }
        const modalSettings = {
            modalHeader: this.translate.instant('CONFIRM.HEADERS.DELETE'),
            modalBody: this.translate.instant('CONFIRM.TEXT.WALLET', {title: wallet.title}),
            buttonSubmit: this.translate.instant('CONFIRM.BUTTONS.YES'),
            buttonClose: this.translate.instant('CONFIRM.BUTTONS.NO'),
        };
        const modal = this.modalService.createFromSettings(modalSettings);
        modal.result.then(data => {
            if (data) {
                if (this.isPrivate) {
                    this.financeService.deletePrivateWallet(wallet.guid).subscribe({
                        next: res => {
                            this.alert.setAlert(res.message, 'green');
                            this.emitRefresh.emit();
                        }, error: err => {
                            this.alert.setAlert(err.message, 'blue');
                        },
                    });
                } else {
                    this.financeService.deleteWallet(wallet.guid).subscribe({
                        next: res => {
                            this.alert.setAlert(res.message, 'green');
                            this.emitRefresh.emit();
                        }, error: err => {
                            this.alert.setAlert(err.message, 'blue');
                        },
                    });
                }
            }
        }).catch(() => {
            return;
        });
    }


    onShowBarterDialog(): void {
        if (this.wallet && this.wallet.active) {
            this.isShowBarterModal = true;
            this.changeMode = true;
        } else {
            this.walletIsNotActiveAlert();
        }
    }

    onShowReplenishmentDialog(): void {
        if (this.wallet.active) {
            this.isShowBarterModal = true;
            this.changeMode = false;
        } else {
            this.walletIsNotActiveAlert();
        }
    }

    walletIsNotActiveAlert(): void {
        this.alert.setAlert(this.translate.instant('ALERTS.BLOCKED_WALLET'), 'blue');
    }

    loadPaymentsHistory(selectWallet: FinanceWalletModel, modal?: TemplateRef<any>): void {
        this.wallet = selectWallet;
        this.financeService.getPaymentsHistory(this.wallet.guid).subscribe(ps => {
            this.wallet.payments = ps.payments?.data;
            this.modalService.openTemplateModal(modal);
        });
    }

    onCloseModal($event: boolean): void {
        if ($event) {
            this.getWallet();
        }
    }
}
